import React from "react";
import { TListingPnlLine, TListingPnlOutput, TListingPnlServiceGroup } from "../../../../domain/codecs/formIO/ListingPnlFormIO";
import { penceToCopyText } from "../../../../shared/src/util";
import { CRMTableHeaderRow } from "../Simple/CRMTableHeaderRow/CRMTableHeaderRow";
import { CRMTableSubTitleRow } from "../Simple/CRMTableSubTitleRow/CRMTableSubTitleRow";
import { CRMTable } from "../Simple/CRMTable/CRMTable";
import { CRMCardOutsideSharpCorners } from "../CRMCardOutsideSharpCorners/CRMCardOutsideSharpCorners";
import { SpacingRow } from "../BuildingBlocks/SpacingRow";
import { SpacingColumn } from "../BuildingBlocks/SpacingColumn";
import { Background } from "../BuildingBlocks/Background";
import { Text } from "../BuildingBlocks/Text";
import { WeightBold } from "../WeightBold/WeightBold";
import { CRMSpacer } from "../CRMSpacer/CRMSpacer";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMColors } from "../../models/CRMColors";

const COLS = "1fr 130px 130px 110px";

const blankOrPence = (pence: number | null): string =>
    pence === null ? "—" : penceToCopyText(pence);

const PnlRow = (props: {
    label: string;
    forecastNetPence: number | null;
    actualNetPence: number | null;
    isActual: boolean | null;
}): JSX.Element => (
    <SpacingRow spacing={CRMSpacing.MEDIUM} childSize={COLS}>
        <Text>{props.label}</Text>
        <Text align="right"><WeightBold>{blankOrPence(props.forecastNetPence)}</WeightBold></Text>
        <Text align="right"><WeightBold>{blankOrPence(props.actualNetPence)}</WeightBold></Text>
        <Text>
            {props.isActual === null
                ? <Text color={CRMColors.NEUTRAL_6}>—</Text>
                : <WeightBold>
                    <Text color={props.isActual ? CRMColors.HIGHLIGHTS_POSITIVE_GREEN_0 : CRMColors.HIGHLIGHTS_PERTINENT_YELLOW_0}>
                        {props.isActual ? "Actual" : "Forecast"}
                    </Text>
                </WeightBold>
            }
        </Text>
    </SpacingRow>
);

const lineRow = (line: TListingPnlLine, label?: string): JSX.Element =>
    <PnlRow
        label={label ?? line.description}
        forecastNetPence={line.forecast_net_pence}
        actualNetPence={line.actual_net_pence}
        isActual={line.is_actual}
    />;

const serviceLabel = (group: TListingPnlServiceGroup): string =>
    group.service_type;

const Section = (props: { title: string; rows: Array<JSX.Element> }): JSX.Element =>
    props.rows.length === 0
        ? <></>
        : <>
            <CRMSpacer size="medium" />
            <CRMTableSubTitleRow title={props.title} />
            <CRMTable<JSX.Element> data={props.rows} rowElement={(row) => row} />
        </>;

const TotalRow = (props: { label: string; pence: number; strong?: boolean }): JSX.Element => (
    <SpacingRow spacing={CRMSpacing.MEDIUM} childSize="1fr 150px">
        <WeightBold><Text size={props.strong ? 16 : 14}>{props.label}</Text></WeightBold>
        <Text align="right">
            <WeightBold><Text size={props.strong ? 16 : 14}>{penceToCopyText(props.pence)}</Text></WeightBold>
        </Text>
    </SpacingRow>
);

export const CRMListingPagePnl = (props: { pnl: TListingPnlOutput }): JSX.Element => {
    const { pnl } = props;

    const incomeRows: Array<JSX.Element> = [
        ...(pnl.ea_fee ? [lineRow(pnl.ea_fee, "Estate agent fee")] : []),
        ...pnl.service_groups.map((g) => lineRow(g.income, serviceLabel(g))),
        ...pnl.other_income.map((l) => lineRow(l)),
    ];
    const costRows: Array<JSX.Element> = [
        ...pnl.service_groups.map((g) => lineRow(g.cost, serviceLabel(g))),
        ...pnl.other_cost.map((l) => lineRow(l)),
    ];
    const marginRows: Array<JSX.Element> = pnl.service_groups.map((g) =>
        <PnlRow
            label={serviceLabel(g)}
            forecastNetPence={g.margin_forecast_net_pence}
            actualNetPence={g.margin_actual_net_pence}
            isActual={null}
        />,
    );

    const isEmpty = incomeRows.length === 0 && costRows.length === 0;

    return (
        <SpacingColumn spacing={CRMSpacing.LARGE}>
            {isEmpty &&
                <Text color={CRMColors.NEUTRAL_4}>No fee, services or invoices on this listing yet.</Text>
            }

            {!isEmpty &&
                <SpacingColumn spacing={CRMSpacing.SMALL}>
                    <CRMTableHeaderRow
                        spacing={CRMSpacing.MEDIUM}
                        spacingRowChildSize={COLS}
                        headers={["Line", "Forecast", "Actual", "Status"]}
                    />
                    <Section title="Income" rows={incomeRows} />
                    <Section title="Costs" rows={costRows} />
                    <Section title="Service margins" rows={marginRows} />
                </SpacingColumn>
            }

            {/* TOTALS */}
            <CRMCardOutsideSharpCorners>
                <Background padding={`${CRMSpacing.MEDIUM} ${CRMSpacing.LARGE}`} color={CRMColors.NEUTRAL_12}>
                    <SpacingColumn spacing={CRMSpacing.SMALL}>
                        <TotalRow label="Forecast total" pence={pnl.total_forecast_net_pence} />
                        <TotalRow label="Actual total" pence={pnl.total_actual_net_pence} />
                        <TotalRow label="Blended total (net)" pence={pnl.total_blended_net_pence} strong={true} />
                    </SpacingColumn>
                </Background>
            </CRMCardOutsideSharpCorners>
        </SpacingColumn>
    );
};
